// client/src/App.js
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { FavoritesProvider } from './contexts/FavoritesContext';
import { Toaster } from 'react-hot-toast';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';


// Common Pages
import Unauthorized from './pages/common/Unauthorized';
import NotFound from './pages/common/NotFound';
import AccountPending from './pages/common/AccountPending'; // ✅ NEW
import FirstLoginGate from './pages/common/FirstLoginGate';
import TermsOfService from './pages/common/TermsOfService';
import PrivacyPolicy from './pages/common/PrivacyPolicy';
import Help from './pages/Help';
import FAQ from './pages/FAQ';
import Pricing from './pages/Pricing';

//Admin Pages
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminApprovals from './pages/admin/AdminApprovals'; // ✅ NEW
import AdminOrders from './pages/admin/AdminOrders';
import AdminTools from './pages/admin/AdminTools';
import AdminSettings from './pages/admin/AdminSettings';
import AdminBilling from './pages/admin/AdminBilling';
import AdminUsers from './pages/admin/AdminUsers';
import AdminAllUsers from './pages/admin/AdminAllUsers';
import AdminBusinessUsers from './pages/admin/AdminBusinessUsers';
import AdminBusinessDetail from './pages/admin/AdminBusinessDetail';
import AdminWholesalerDetail from './pages/admin/AdminWholesalerDetail';
import AdminWholesalers from './pages/admin/AdminWholesalers';
import AdminOrderDetail from './pages/admin/AdminOrderDetail';
import AdminProducts from './pages/admin/AdminProducts';
import AdminFlashOffers from './pages/admin/AdminFlashOffers';
import AdminDiscounts from './pages/admin/AdminDiscounts';
import AdminAnalytics from './pages/admin/AdminAnalytics';
import AdminAnnouncements from './pages/admin/AdminAnnouncements';
import AdminCatalogDiscounts from './pages/admin/AdminCatalogDiscounts';
import ConciergeOnboarding from './pages/admin/ConciergeOnboarding';
import ConciergeOnboardingNew from './pages/admin/ConciergeOnboardingNew';
import ConciergeOnboardingDetail from './pages/admin/ConciergeOnboardingDetail';

// Business Pages
import Home from './pages/business/Home';
import BusinessLogin from './pages/business/Login';
import RegisterBusiness from './pages/business/Register';
import Wholesalers from './pages/business/Wholesalers';
import ProductDetails from './pages/business/ProductDetails';
import MyOrders from './pages/business/MyOrders';
import Cart from './pages/business/Cart';
import BusinessDashboard from './pages/business/Dashboard';
import BusinessProfile from './pages/business/BusinessProfile';
import BusinessAnalytics from './pages/business/BusinessAnalytics';
import Favorites from './pages/business/Favorites';
import OrderTemplates from './pages/business/OrderTemplates';
import FlashOffersFeed from './pages/business/FlashOffersFeed';
import SearchResults from './pages/business/SearchResults';

// Wholesaler Pages
import ProductForm from './pages/wholesaler/ProductForm';
import ProductManagement from './pages/wholesaler/ProductManagement';
import BulkProductUpload from './pages/wholesaler/BulkProductUpload';
import WholesalerLogin from './pages/wholesaler/Login';
import RegisterWholesaler from './pages/wholesaler/Register';
import WholesalerDashboard from './pages/wholesaler/Dashboard';
import WholesalerDetail from './pages/wholesaler/WholesalerDetail';
import WholesalerOrders from './pages/wholesaler/WholesalerOrders';
import WholesalerProfile from './pages/wholesaler/WholesalerProfile';
import WholesalerAnalytics from './pages/wholesaler/WholesalerAnalytics';
import WholesalerPricing from './pages/wholesaler/WholesalerPricing';
import WholesalerSubscription from './pages/wholesaler/WholesalerSubscription';
import CustomerDiscounts from './pages/wholesaler/CustomerDiscounts';
import CatalogRequests from './pages/wholesaler/CatalogRequests';
import FlashOffers from './pages/wholesaler/FlashOffers';
import CatalogDiscounts from './pages/wholesaler/CatalogDiscounts';
import ProductDiscounts from './pages/wholesaler/ProductDiscounts';
import TeamManagement from './pages/wholesaler/TeamManagement';
import AcceptInvite from './pages/wholesaler/AcceptInvite';
import OnBehalfOrder from './pages/wholesaler/OnBehalfOrder';


// Protected Route Component
import ProtectedRoute from './components/common/ProtectedRoute';

function App() {
  return (
    <AuthProvider>
      <Router>
        <FavoritesProvider>
          <CartProvider>
            <Toaster position="top-center" toastOptions={{ duration: 3000, style: { borderRadius: '8px', padding: '12px 16px' } }} />
            <div className="App">
              <Routes>
              {/* Public routes */}
              <Route path="/" element={<Home />} />
              <Route path="/login" element={<BusinessLogin />} />
              <Route path="/register" element={<RegisterBusiness />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />
              <Route path="/account-pending" element={<AccountPending />} /> {/* ✅ NEW */}
              <Route path="/help" element={<Help />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/wholesalers" element={<Wholesalers />} />
              <Route path="/wholesalers/:id" element={<WholesalerDetail />} />
              <Route path="/products/:id" element={<ProductDetails />} />

              {/* Business protected routes */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute requiredRole="business">
                    <BusinessDashboard />
                  </ProtectedRoute>
                }
              />

              <Route 
                path="/business/templates"
                element={
                  <ProtectedRoute requiredRole="business">
                    <OrderTemplates />
                  </ProtectedRoute>
                } 
              />

              <Route
                path="/business/profile"
                element={
                  <ProtectedRoute requiredRole="business">
                    <BusinessProfile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/business/products"
                element={
                  <ProtectedRoute requiredRole="business">
                    <Wholesalers />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/business/orders"
                element={
                  <ProtectedRoute requiredRole="business">
                    <MyOrders />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/my-orders"
                element={
                  <ProtectedRoute requiredRole="business">
                    <MyOrders />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cart"
                element={
                  <ProtectedRoute requiredRole="business">
                    <Cart />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/business/analytics"
                element={
                  <ProtectedRoute requiredRole="business">
                    <BusinessAnalytics />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/business/favorites"
                element={
                  <ProtectedRoute requiredRole="business">
                    <Favorites />
                  </ProtectedRoute>
                }
              />
              <Route path="/favorites" element={<Navigate to="/business/favorites" replace />} />
              <Route path="/templates" element={<Navigate to="/business/templates" replace />} />
              
              <Route
                path="/search"
                element={
                  <ProtectedRoute requiredRole="business">
                    <SearchResults />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/offers"
                element={
                  <ProtectedRoute requiredRole="business">
                    <FlashOffersFeed />
                  </ProtectedRoute>
                }
              />

              {/* Wholesaler protected routes */}
              <Route path="/wholesaler" element={<Navigate to="/wholesaler/login" replace />} />
              <Route path="/wholesaler/login" element={<WholesalerLogin />} />
              <Route path="/wholesaler/register" element={<RegisterWholesaler />} />

              <Route
                path="/wholesaler/dashboard"
                element={
                  <ProtectedRoute requiredRole="wholesaler">
                    <WholesalerDashboard />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/wholesaler/profile"
                element={
                  <ProtectedRoute requiredRole="wholesaler">
                    <WholesalerProfile />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/wholesaler/orders"
                element={
                  <ProtectedRoute requiredRole="wholesaler">
                    <WholesalerOrders />
                  </ProtectedRoute>
                }
              /> 

              <Route
                path="/wholesaler/discounts"
                element={
                  <ProtectedRoute requiredRole="wholesaler">
                    <CustomerDiscounts />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/wholesaler/products"
                element={
                  <ProtectedRoute requiredRole="wholesaler">
                    <ProductManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/wholesaler/products/new"
                element={
                  <ProtectedRoute requiredRole="wholesaler">
                    <ProductForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/wholesaler/products/edit/:id"
                element={
                  <ProtectedRoute requiredRole="wholesaler">
                    <ProductForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/wholesaler/products/bulk-upload"
                element={
                  <ProtectedRoute requiredRole="wholesaler">
                    <BulkProductUpload />
                  </ProtectedRoute>
                }
              />

              {/* Pricing Page Route */}
              <Route
                path="/wholesaler/pricing"
                element={
                  <ProtectedRoute requiredRole="wholesaler">
                    <WholesalerPricing />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/wholesaler/analytics"
                element={
                  <ProtectedRoute requiredRole="wholesaler">
                    <WholesalerAnalytics />
                  </ProtectedRoute>
                }
              />

              <Route 
                path="/wholesaler/subscription" 
                element={
                  <ProtectedRoute allowedRoles={['wholesaler']}>
                    <WholesalerSubscription />
                  </ProtectedRoute>
                } 
              />
              
              <Route 
                path="/wholesaler/catalog-requests" 
                element={
                  <ProtectedRoute allowedRoles={['wholesaler']}>
                    <CatalogRequests />
                  </ProtectedRoute>
                } 
              />

              <Route path="/wholesaler/catalog-discounts" element={<CatalogDiscounts />} />

              <Route
                path="/wholesaler/product-discounts"
                element={
                  <ProtectedRoute allowedRoles={['wholesaler']}>
                    <ProductDiscounts />
                  </ProtectedRoute>
                }
              />


              <Route 
                path="/wholesaler/flash-offers" 
                element={
                  <ProtectedRoute allowedRoles={['wholesaler']}>
                    <FlashOffers />
                  </ProtectedRoute>
                } 
              />
              
              <Route path="/wholesaler/accept-invite/:token" element={<AcceptInvite />} />
              <Route
                path="/wholesaler/team"
                element={
                  <ProtectedRoute requiredRole="wholesaler">
                    <TeamManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/wholesaler/place-order"
                element={
                  <ProtectedRoute requiredRole="wholesaler">
                    <OnBehalfOrder />
                  </ProtectedRoute>
                }
              />

              {/* Admin routes */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route
                path="/admin/dashboard"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />
              {/* ✅ NEW - Admin Approvals Route */}
              <Route
                path="/admin/approvals"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AdminApprovals />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/users/all"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AdminAllUsers />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/users/businesses"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AdminBusinessUsers />
                  </ProtectedRoute>
                }
              />

              <Route path="/admin/orders" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminOrders />
                </ProtectedRoute>
              } />

              <Route path="/admin/order/:id" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminOrderDetail />
                </ProtectedRoute>
              } />
              <Route
                path="/admin/tools"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AdminTools />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/notifications"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AdminTools />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/settings"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AdminSettings />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/billing"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AdminBilling />
                  </ProtectedRoute>
                }
              />

              <Route path="/admin/products" element={<ProtectedRoute requiredRole="admin"><AdminProducts /></ProtectedRoute>} />
              <Route path="/admin/flash-offers" element={<ProtectedRoute requiredRole="admin"><AdminFlashOffers /></ProtectedRoute>} />
              <Route path="/admin/discounts" element={<ProtectedRoute requiredRole="admin"><AdminDiscounts /></ProtectedRoute>} />
              <Route path="/admin/catalog-discounts" element={<ProtectedRoute requiredRole="admin"><AdminCatalogDiscounts /></ProtectedRoute>} />
              <Route path="/admin/analytics" element={<ProtectedRoute requiredRole="admin"><AdminAnalytics /></ProtectedRoute>} />
              <Route path="/admin/announcements" element={<ProtectedRoute requiredRole="admin"><AdminAnnouncements /></ProtectedRoute>} />


              {/* Admin User Management Detail Views */}
              <Route path="/admin/business/:id" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminBusinessDetail />
                </ProtectedRoute>
              } />

              <Route path="/admin/wholesaler/:id" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminWholesalerDetail />
                </ProtectedRoute>
              } />

              <Route path="/admin/wholesalers" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminWholesalers />
                </ProtectedRoute>
              } />

              {/* Concierge Onboarding */}
              <Route path="/admin/onboarding" element={
                <ProtectedRoute requiredRole="admin">
                  <ConciergeOnboarding />
                </ProtectedRoute>
              } />
              <Route path="/admin/onboarding/new" element={
                <ProtectedRoute requiredRole="admin">
                  <ConciergeOnboardingNew />
                </ProtectedRoute>
              } />
              <Route path="/admin/onboarding/:batchId" element={
                <ProtectedRoute requiredRole="admin">
                  <ConciergeOnboardingDetail />
                </ProtectedRoute>
              } />

              {/* First-login onboarding gate (any authenticated role) */}
              <Route
                path="/first-login"
                element={
                  <ProtectedRoute>
                    <FirstLoginGate />
                  </ProtectedRoute>
                }
              />

              {/* Common routes */}
              <Route path="/unauthorized" element={<Unauthorized />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </div>
        </CartProvider>
      </FavoritesProvider>
    </Router>
    </AuthProvider>
  );
}

export default App;